import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, hasSuperAdminAccess } from '../../contexts/AuthContext';
import { adminActionLogService } from '../../api/services/AdminActionLogService';
import type {
  AdminActionLogListItemViewModel,
  AdminActionLogQueryFilter,
  AdminActionType,
  AdminActionTargetType,
} from '../../api/types/adminActionLog';
import {
  ADMIN_ACTION_TYPES,
  ADMIN_ACTION_TARGET_TYPES,
} from '../../api/types/adminActionLog';

const DEFAULT_PAGE_SIZE = 50;
const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 200;

/**
 * Turkish labels for the stable action codes (presentation only — the backend
 * always returns the raw codes per contract §5 / FR-014).
 */
const ACTION_LABELS: Record<AdminActionType, string> = {
  ListingApproved: 'İlan Onaylandı',
  ListingRejected: 'İlan Reddedildi',
  UserBanned: 'Kullanıcı Engellendi',
  UserUnbanned: 'Engelleme Kaldırıldı',
  SupportTicketStarted: 'Destek Başlatıldı',
  SupportTicketResolved: 'Destek Çözümlendi',
};

const TARGET_LABELS: Record<AdminActionTargetType, string> = {
  Listing: 'İlan',
  User: 'Kullanıcı',
  SupportTicket: 'Destek Talebi',
};

function formatActionLabel(code: string): string {
  return (ACTION_LABELS as Record<string, string>)[code] ?? code;
}

function formatTargetTypeLabel(code: string): string {
  return (TARGET_LABELS as Record<string, string>)[code] ?? code;
}

function formatOccurredAt(isoUtc: string): string {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return isoUtc;
  return date.toLocaleString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Convert the browser-local `<input type="datetime-local">` value to UTC ISO. */
function localInputToIsoUtc(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

/** Convert a stored ISO UTC string back to a `datetime-local` input value in the browser TZ. */
function isoUtcToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface FilterFormState {
  actionType: string;
  actorUserId: string;
  targetType: string;
  targetId: string;
  from: string;
  to: string;
}

function readFormFromParams(params: URLSearchParams): FilterFormState {
  return {
    actionType: params.get('actionType') ?? '',
    actorUserId: params.get('actorUserId') ?? '',
    targetType: params.get('targetType') ?? '',
    targetId: params.get('targetId') ?? '',
    from: isoUtcToLocalInput(params.get('from')),
    to: isoUtcToLocalInput(params.get('to')),
  };
}

function readFilterFromParams(params: URLSearchParams): AdminActionLogQueryFilter {
  const filter: AdminActionLogQueryFilter = {};
  const actionType = params.get('actionType');
  if (actionType && (ADMIN_ACTION_TYPES as readonly string[]).includes(actionType)) {
    filter.actionType = actionType as AdminActionType;
  }
  const actorUserId = params.get('actorUserId');
  if (actorUserId) filter.actorUserId = actorUserId;

  const targetType = params.get('targetType');
  if (targetType && (ADMIN_ACTION_TARGET_TYPES as readonly string[]).includes(targetType)) {
    filter.targetType = targetType as AdminActionTargetType;
  }
  const targetId = params.get('targetId');
  if (targetId) filter.targetId = targetId;

  const from = params.get('from');
  if (from) filter.from = from;
  const to = params.get('to');
  if (to) filter.to = to;

  const pageNumber = Number.parseInt(params.get('pageNumber') ?? '', 10);
  filter.pageNumber = Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1;

  const pageSize = Number.parseInt(params.get('pageSize') ?? '', 10);
  filter.pageSize =
    Number.isFinite(pageSize) && pageSize > 0
      ? Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, pageSize))
      : DEFAULT_PAGE_SIZE;

  return filter;
}

function buildTargetLink(row: AdminActionLogListItemViewModel): string | null {
  if (!row.targetExists) return null;
  switch (row.targetType) {
    case 'Listing':
      return `/ilan-detay/${row.targetId}`;
    case 'User':
      return `/satici-profil/${row.targetId}`;
    case 'SupportTicket':
      return `/admin-panel/support-tickets/${row.targetId}`;
    default:
      return null;
  }
}

const AdminActionLogPage: React.FC = () => {
  const { user } = useAuth();
  const allowed = hasSuperAdminAccess(user);

  const [searchParams, setSearchParams] = useSearchParams();
  const [formState, setFormState] = useState<FilterFormState>(() => readFormFromParams(searchParams));

  const [rows, setRows] = useState<AdminActionLogListItemViewModel[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);

  const activeFilter = useMemo(() => readFilterFromParams(searchParams), [searchParams]);

  const refresh = useCallback(
    async (filter: AdminActionLogQueryFilter) => {
      if (!allowed) return;
      setLoading(true);
      setError(null);
      setForbidden(false);
      try {
        const result = await adminActionLogService.list(filter);
        if (!result.isSuccess) {
          if (result.statusCode === 403) {
            setForbidden(true);
            setRows([]);
            setTotalPages(0);
            setTotalCount(0);
            return;
          }
          setError(result.message ?? 'Kayıtlar yüklenemedi.');
          setRows([]);
          setTotalPages(0);
          setTotalCount(0);
          return;
        }
        const payload = result.data;
        if (!payload) {
          setRows([]);
          setTotalPages(0);
          setTotalCount(0);
          return;
        }
        setRows(payload.items);
        setPageNumber(payload.page);
        setPageSize(payload.pageSize);
        setTotalPages(payload.totalPages);
        setTotalCount(payload.totalCount);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Kayıtlar yüklenemedi.');
        setRows([]);
        setTotalPages(0);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [allowed],
  );

  useEffect(() => {
    setFormState(readFormFromParams(searchParams));
    void refresh(activeFilter);
  }, [searchParams, activeFilter, refresh]);

  const updateSearchParams = useCallback(
    (next: AdminActionLogQueryFilter) => {
      const params = new URLSearchParams();
      if (next.actionType) params.set('actionType', next.actionType);
      if (next.actorUserId) params.set('actorUserId', next.actorUserId);
      if (next.targetType) params.set('targetType', next.targetType);
      if (next.targetId) params.set('targetId', next.targetId);
      if (next.from) params.set('from', next.from);
      if (next.to) params.set('to', next.to);
      if (next.pageNumber && next.pageNumber > 1) {
        params.set('pageNumber', String(next.pageNumber));
      }
      if (next.pageSize && next.pageSize !== DEFAULT_PAGE_SIZE) {
        params.set('pageSize', String(next.pageSize));
      }
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const handleSubmit = useCallback(
    (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      const next: AdminActionLogQueryFilter = {
        pageNumber: 1,
        pageSize,
      };
      if (formState.actionType && (ADMIN_ACTION_TYPES as readonly string[]).includes(formState.actionType)) {
        next.actionType = formState.actionType as AdminActionType;
      }
      if (formState.actorUserId.trim()) next.actorUserId = formState.actorUserId.trim();
      if (formState.targetType && (ADMIN_ACTION_TARGET_TYPES as readonly string[]).includes(formState.targetType)) {
        next.targetType = formState.targetType as AdminActionTargetType;
      }
      if (formState.targetId.trim()) next.targetId = formState.targetId.trim();
      const fromIso = localInputToIsoUtc(formState.from);
      if (fromIso) next.from = fromIso;
      const toIso = localInputToIsoUtc(formState.to);
      if (toIso) next.to = toIso;
      updateSearchParams(next);
    },
    [formState, pageSize, updateSearchParams],
  );

  const handleClear = useCallback(() => {
    setFormState({ actionType: '', actorUserId: '', targetType: '', targetId: '', from: '', to: '' });
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const goToPage = useCallback(
    (target: number) => {
      if (target < 1 || (totalPages > 0 && target > totalPages)) return;
      updateSearchParams({ ...activeFilter, pageNumber: target, pageSize });
    },
    [activeFilter, pageSize, totalPages, updateSearchParams],
  );

  if (!allowed) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-ban me-2" />
        Bu sayfayı görüntüleme yetkiniz yok.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="mb-0">
          <i className="fas fa-clipboard-list me-2 text-primary" />
          Yönetici Logları
        </h2>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3 align-items-end" noValidate>
            <div className="col-md-3">
              <label htmlFor="admin-log-action" className="form-label">Aksiyon</label>
              <select
                id="admin-log-action"
                className="form-select"
                value={formState.actionType}
                onChange={(e) => setFormState((s) => ({ ...s, actionType: e.target.value }))}
              >
                <option value="">Tümü</option>
                {ADMIN_ACTION_TYPES.map((code) => (
                  <option key={code} value={code}>{ACTION_LABELS[code]}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label htmlFor="admin-log-target-type" className="form-label">Hedef Türü</label>
              <select
                id="admin-log-target-type"
                className="form-select"
                value={formState.targetType}
                onChange={(e) => setFormState((s) => ({ ...s, targetType: e.target.value }))}
              >
                <option value="">Tümü</option>
                {ADMIN_ACTION_TARGET_TYPES.map((code) => (
                  <option key={code} value={code}>{TARGET_LABELS[code]}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label htmlFor="admin-log-actor" className="form-label">Yönetici Kullanıcı Id</label>
              <input
                id="admin-log-actor"
                type="text"
                className="form-control"
                placeholder="UUID"
                value={formState.actorUserId}
                onChange={(e) => setFormState((s) => ({ ...s, actorUserId: e.target.value }))}
              />
            </div>

            <div className="col-md-3">
              <label htmlFor="admin-log-target-id" className="form-label">Hedef Id</label>
              <input
                id="admin-log-target-id"
                type="text"
                className="form-control"
                placeholder="UUID"
                value={formState.targetId}
                onChange={(e) => setFormState((s) => ({ ...s, targetId: e.target.value }))}
              />
            </div>

            <div className="col-md-4">
              <label htmlFor="admin-log-from" className="form-label">Başlangıç</label>
              <input
                id="admin-log-from"
                type="datetime-local"
                className="form-control"
                value={formState.from}
                onChange={(e) => setFormState((s) => ({ ...s, from: e.target.value }))}
              />
            </div>

            <div className="col-md-4">
              <label htmlFor="admin-log-to" className="form-label">Bitiş</label>
              <input
                id="admin-log-to"
                type="datetime-local"
                className="form-control"
                value={formState.to}
                onChange={(e) => setFormState((s) => ({ ...s, to: e.target.value }))}
              />
            </div>

            <div className="col-md-4 d-flex gap-2">
              <button type="submit" className="btn btn-primary flex-grow-1" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Yükleniyor…
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Filtrele
                  </>
                )}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleClear} disabled={loading}>
                Temizle
              </button>
            </div>
          </form>
        </div>
      </div>

      {forbidden && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-ban me-2" />
          Bu sayfayı görüntüleme yetkiniz yok.
        </div>
      )}

      {error && !forbidden && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped align-middle mb-0">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th>Zaman</th>
                  <th>Yönetici</th>
                  <th>Aksiyon</th>
                  <th>Hedef Türü</th>
                  <th>Hedef</th>
                  <th>Neden</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && !error && !forbidden && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      Kayıt bulunamadı.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const targetLink = buildTargetLink(row);
                  const label = row.targetLabel ?? row.targetId;
                  return (
                    <tr key={row.id}>
                      <td className="text-nowrap">{formatOccurredAt(row.occurredAtUtc)}</td>
                      <td>{row.actorDisplayName}</td>
                      <td>{formatActionLabel(row.actionType)}</td>
                      <td>{formatTargetTypeLabel(row.targetType)}</td>
                      <td>
                        {targetLink ? (
                          <Link to={targetLink}>{label}</Link>
                        ) : (
                          <span className="text-muted" title="Hedef silinmiş">{label}</span>
                        )}
                      </td>
                      <td>{row.reason ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 0 && (
            <div className="d-flex align-items-center justify-content-between mt-3">
              <div className="text-muted small">
                Toplam {totalCount.toLocaleString('tr-TR')} kayıt • Sayfa {pageNumber} / {totalPages}
              </div>
              <div className="btn-group" role="group" aria-label="Sayfalama">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(pageNumber - 1)}
                  disabled={loading || pageNumber <= 1}
                >
                  <i className="fas fa-chevron-left me-1" />
                  Önceki
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(pageNumber + 1)}
                  disabled={loading || pageNumber >= totalPages}
                >
                  Sonraki
                  <i className="fas fa-chevron-right ms-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActionLogPage;
